import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Bell, User as UserIcon, Settings, LogOut, Plus, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useAuthStore } from '@/app/store/authStore';
import { useTaskStore } from '@/app/store/taskStore';
import { ProfilePopup } from '@/app/pages/profile/components/ProfilePopup';
import { SyncStatus } from '@/app/components/SyncStatus';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { taskService } from '@/app/services/taskService';

interface ClientManagerHeaderProps {
  isMobile?: boolean;
}

const ClientManagerHeader: React.FC<ClientManagerHeaderProps> = ({ isMobile }) => {
  const { logoutAction, user } = useAuthStore();
  const { setSearchQuery, openCreateModal } = useTaskStore();
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleLogout = (): void => {
    logoutAction();
  };

  const handleProfileClick = (): void => {
    setIsProfilePopupOpen(true);
  };

  // Get initials for Avatar Fallback
  const getInitials = (name?: string | null): string => {
    if (!name) return 'U';
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  };
  const userInitials = getInitials(user?.full_name || user?.username);

  // Fetch task counts by status with 30-second refresh interval
  const { data: taskCounts } = useQuery({
    queryKey: ['taskCounts'],
    queryFn: async () => {
      const [openTasks, completedTasks] = await Promise.all([
        taskService.list({ status: 'open' as any, limit: 1, skip: 0 }),
        taskService.list({ status: 'completed' as any, limit: 1, skip: 0 }),
      ]);

      return {
        open: openTasks.total || 0,
        completed: completedTasks.total || 0,
        total: (openTasks.total || 0) + (completedTasks.total || 0),
      };
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 25000,
  });

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchQuery(value);
  };

  // Handle quick create task button
  const handleQuickCreateTask = () => {
    openCreateModal();
  };

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-30 border-b bg-background',
          'px-4 py-2 sm:px-6 sm:py-3',
          isMobile && 'pt-0'
        )}
      >
        {/* Top row with search and user actions */}
        <div className="flex items-center gap-2">
          {/* Task Search */}
          <div className={cn('relative flex-1', 'max-w-full sm:max-w-[300px] md:max-w-[400px]')}>
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={isMobile ? 'Search tasks...' : 'Search tasks by title or description...'}
              className="w-full rounded-lg bg-muted pl-8 text-sm sm:text-base"
              value={searchValue}
              onChange={handleSearchChange}
            />
          </div>

          {/* Task Status Counters - Desktop only */}
          <div className="hidden sm:flex items-center gap-2">
            <Badge
              variant="outline"
              className="gap-1 bg-primary/5 text-primary hover:bg-primary/10 border-primary/30 dark:border-primary"
            >
              <Clock className="h-3 w-3" />
              <span className="text-xs font-medium">Open:</span>
              <span className="text-xs font-bold">{taskCounts?.open || 0}</span>
            </Badge>

            <Badge
              variant="outline"
              className="gap-1 bg-success/5 text-success hover:bg-success/10 border-success/30 dark:border-success"
            >
              <CheckCircle2 className="h-3 w-3" />
              <span className="text-xs font-medium">Completed:</span>
              <span className="text-xs font-bold">{taskCounts?.completed || 0}</span>
            </Badge>
          </div>

          {/* Quick Create Task Button */}
          <Button onClick={handleQuickCreateTask} size="sm" className="gap-1 hidden sm:flex">
            <Plus className="h-4 w-4" />
            <span className="hidden md:inline">New Task</span>
          </Button>

          {/* Mobile Quick Create */}
          <Button onClick={handleQuickCreateTask} size="icon" variant="default" className="sm:hidden">
            <Plus className="h-4 w-4" />
          </Button>

          <div className="hidden sm:block h-8 w-px bg-border" />

          {/* Sync Status */}
          <SyncStatus />

          {/* Notifications */}
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {/* Notification badge - could be enhanced to show actual count */}
            {/* <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-destructive" /> */}
          </Button>

          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-medium">{user?.full_name || user?.username}</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {user?.role?.name?.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleProfileClick}>
                <UserIcon className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Profile Popup Modal */}
      <ProfilePopup isOpen={isProfilePopupOpen} onOpenChange={setIsProfilePopupOpen} />
    </>
  );
};

export default ClientManagerHeader;
